// TypeScript interfaces and enums for ShipDto and related types, matching backend DTOs

export interface ShipDto {
  id: string;
  name: string;
  description: string;
  specificationDescription?: string;
  specificationsHeader?: string;
  specificationNote?: string;
  type: ShipType;
  category: ShipCategory;
  status: ShipStatus;
  location: ShipLocation;
  currentCargoLoad: number;
  currentShieldValue: number;
  currentHullValue: number;
  baseShieldCapacity: number;
  baseHullCapacity: number;
  baseMiningSpeed: number;
  baseWarpSpeed: number;
  baseUnloadSpeed: number;
  baseCargoCapacity: number;
  miningSpeed: number;
  effectiveWarpSpeed: number;
  effectiveUnloadSpeed: number;
  effectiveCargoCapacity: number;
  effectiveMiningYield: number;
  timeSinceStatusChange: number;
  estimatedTimeToComplete?: number;
  pendingUpgrade?: UpgradeDto;
  pendingUpgradeBranch?: string;
  upgradeTree?: {
    branches: Array<{
      branchType: string;
      originalBranchType: string | number;
      upgrades: UpgradeDto[];
    }>;
  };
  nextShipCost?: number; // Added to match backend
}

export interface UpgradeDto {
  id: string;
  name: string;
  description: string;
  type: UpgradeType;
  level: number;
  bonusValue: number;
  cost: number;
  isUnlocked: boolean;
}

// Backend-aligned enums as const objects for compatibility
export const ShipType = {
  Mining: 10,
  Barge: 11,
  Command: 20,
  Hauler: 30,
  Salvager: 31,
  Combat: 32,
  Mothership: 40,
  Freighter: 50,
} as const;
export type ShipType = (typeof ShipType)[keyof typeof ShipType];

export const ShipCategory = {
  Mining: 0,
  Command: 1,
  Hauler: 2,
  Support: 3,
  Mothership: 4,
} as const;
export type ShipCategory = (typeof ShipCategory)[keyof typeof ShipCategory];

export const ShipStatus = {
  AwaitingOrders: 0,
  Mining: 1,
  WarpingToMothership: 2,
  WarpingToAsteroidBelt: 3,
  OffloadingCargo: 4,
  Upgrading: 5,
  WarpingToMothershipForUpgrade: 6,
  MiningToHauler: 7,
  Repairing: 8,
  WarpingToMothershipForRepair: 9,
  WarpingToMothershipForOrders: 10,
  UnloadingBeforeAwaitingOrders: 11,
  Disabled: 12,
} as const;
export type ShipStatus = (typeof ShipStatus)[keyof typeof ShipStatus];

export const ShipLocation = {
  Mothership: 0,
  AsteroidBelt: 1,
  Warping: 2,
} as const;
export type ShipLocation = (typeof ShipLocation)[keyof typeof ShipLocation];

export const UpgradeType = {
  Mining: "Mining",
  Engines: "Engines",
  Cargo: "Cargo",
  CommandBoostBonus: "CommandBoostBonus",
  FleetSize: "FleetSize",
} as const;
export type UpgradeType = (typeof UpgradeType)[keyof typeof UpgradeType];

// --- Normalization helpers ---
export function normalizeShipDto(raw: any): ShipDto {
  // Convert string enums to numbers if needed
  let type = raw.type;
  if (typeof type === 'string') {
    if (ShipType[type as keyof typeof ShipType] !== undefined) {
      type = ShipType[type as keyof typeof ShipType];
    } else {
      type = parseInt(type, 10);
    }
  }
  let category = raw.category;
  if (typeof category === 'string' && ShipCategory[category as keyof typeof ShipCategory] !== undefined) {
    category = ShipCategory[category as keyof typeof ShipCategory];
  }
  let status = raw.status;
  if (typeof status === 'string' && ShipStatus[status as keyof typeof ShipStatus] !== undefined) {
    status = ShipStatus[status as keyof typeof ShipStatus];
  }
  let location = raw.location;
  if (typeof location === 'string' && ShipLocation[location as keyof typeof ShipLocation] !== undefined) {
    location = ShipLocation[location as keyof typeof ShipLocation];
  }
  return {
    ...raw,
    type,
    category,
    status,
    location,
    pendingUpgrade: raw.pendingUpgrade ? normalizeUpgradeDto(raw.pendingUpgrade) : undefined,
    upgradeTree: raw.upgradeTree ? normalizeUpgradeTreeDto(raw.upgradeTree) : undefined,
    nextShipCost: raw.nextShipCost ?? raw.NextShipCost,
  };
}

export function normalizeUpgradeDto(raw: any): UpgradeDto {
  // Convert string enums to correct UpgradeType
  let type = raw.type;
  if (typeof type === 'string') {
    type = UpgradeType[type as keyof typeof UpgradeType] || type;
  } else if (typeof type === 'number') {
    // Map integer enums to string values
    if (type === 1) type = UpgradeType.Mining;
    else if (type === 2) type = UpgradeType.Engines;
    else if (type === 3) type = UpgradeType.Cargo;
    else if (type === 4) type = UpgradeType.FleetSize;
    else if (type === 5) type = "Command";
    else if (type === 6) type = "Engineering";
  }

  return {
    ...raw,
    id: raw.id || raw.Id,
    name: raw.name || raw.Name,
    description: raw.description || raw.Description,
    level: raw.level ?? raw.Level,
    bonusValue: raw.bonusValue ?? raw.BonusValue,
    cost: raw.cost ?? raw.Cost,
    isUnlocked: raw.isUnlocked ?? raw.IsUnlocked,
    type: String(type), // Ensure it's a string
  };
}

export function normalizeUpgradeTreeDto(raw: any): { branches: Array<{ branchType: string; originalBranchType: string | number; upgrades: UpgradeDto[] }> } {
  if (!raw || !Array.isArray(raw.branches)) return { branches: [] };

  return {
    branches: raw.branches.map((branch: any) => {
      let branchType = branch.branchType;
      const originalBranchType = branch.branchType;

      // Map integer enums to string values if needed
      if (typeof branchType === 'number' || !isNaN(Number(branchType))) {
        const val = Number(branchType);
        if (val === 1) branchType = UpgradeType.Mining;
        else if (val === 2) branchType = UpgradeType.Engines;
        else if (val === 3) branchType = UpgradeType.Cargo;
        else if (val === 4) branchType = UpgradeType.FleetSize;
        else if (val === 5) branchType = "Command"; // Not in UpgradeType const yet?
        else if (val === 6) branchType = "Engineering";
      }

      return {
        ...branch,
        branchType: String(branchType),
        originalBranchType,
        upgrades: Array.isArray(branch.upgrades) ? branch.upgrades.map(normalizeUpgradeDto) : []
      };
    })
  };
}

// --- Enum display helpers ---
export function getShipTypeString(type: ShipType): string {
  const entry = Object.entries(ShipType).find(([, v]) => v === type);
  return entry ? entry[0] : String(type);
}

export function getShipCategoryString(category: ShipCategory): string {
  const entry = Object.entries(ShipCategory).find(([, v]) => v === category);
  return entry ? entry[0] : String(category);
}

export function getShipStatusString(status: ShipStatus): string {
  const entry = Object.entries(ShipStatus).find(([, v]) => v === status);
  return entry ? entry[0] : String(status);
}

export function getShipLocationString(location: ShipLocation): string {
  const entry = Object.entries(ShipLocation).find(([, v]) => v === location);
  return entry ? entry[0] : String(location);
}

// --- Normalization helpers ---
export interface ShipPurchaseOption {
  type: string;
  originalType: string | number; // Store the original backend type for API calls
  name: string;
  description: string;
  specificationDescription?: string;
  specificationsHeader?: string;
  specificationNote?: string;
  cost: number;
  iconUrl?: string;
  // Base stats for purchase options
  baseMiningSpeed?: number;
  baseCargoCapacity?: number;
  baseWarpSpeed?: number;
  baseUnloadSpeed?: number;
  fleetBonus?: number;
}

export function normalizeShipPurchaseOption(raw: any): ShipPurchaseOption {
  let type = (raw.type || raw.Type || '').toString();
  const originalType = raw.type || raw.Type; // Keep original for API calls

  // Map backend enum values/names to frontend icon keys
  if (type === '10' || type === 'Mining') type = 'mining-frigate';
  else if (type === '11' || type === 'Barge') type = 'barge';
  else if (type === '20' || type === 'Command') type = 'command-ship';
  else if (type === '30' || type === 'Hauler') type = 'hauler-ship';
  else if (type === '50' || type === 'Freighter') type = 'freighter';

  return {
    type,
    originalType,
    name: raw.name || raw.Name || '',
    description: raw.description || raw.Description || '',
    specificationDescription: raw.specificationDescription || raw.SpecificationDescription || '',
    specificationsHeader: raw.specificationsHeader || raw.SpecificationsHeader || '',
    specificationNote: raw.specificationNote || raw.SpecificationNote || '',
    cost: raw.cost ?? raw.Cost ?? 0,
    iconUrl: raw.iconUrl || raw.IconUrl || undefined,
    // Base stats
    baseMiningSpeed: raw.baseMiningSpeed ?? raw.BaseMiningSpeed ?? 0,
    baseCargoCapacity: raw.baseCargoCapacity ?? raw.BaseCargoCapacity ?? 0,
    baseWarpSpeed: raw.baseWarpSpeed ?? raw.BaseWarpSpeed ?? 0,
    baseUnloadSpeed: raw.baseUnloadSpeed ?? raw.BaseUnloadSpeed ?? 0,
    fleetBonus: raw.fleetBonus ?? raw.FleetBonus ?? 0,
  };
}
